import { useState } from "react";
import api from '../../api/api';
import TextInput from "../shared/textInput";
import validation from "../../helpers/validation";
import Button from "../shared/button";
import './users.css';
import { useNavigate } from "react-router";
import { useUser } from "../../context/UserContext";
import { Link } from 'react-router-dom';

const SignUp = () => {
    const [display, setDisplay] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('host');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const navigate = useNavigate();
    const { setUser } = useUser();

    const send = async (event) => {
        if (event?.preventDefault) event.preventDefault();
        if (submitting) return;

        const normalizedEmail = email.trim().toLowerCase();
        const normalizedDisplay = display.trim();
        const normalizedFirstName = firstName.trim();
        const normalizedLastName = lastName.trim();

        if (!validation.validateEmail(normalizedEmail)) {
            setMessage({ type: 'error', text: 'Please enter a valid email address.' });
            return;
        }

        if (password.length < 6) {
            setMessage({ type: 'error', text: 'Please use at least six characters in your password.' });
            return;
        }

        if (!normalizedFirstName || !normalizedLastName || !normalizedDisplay) {
            setMessage({ type: 'error', text: 'Please input your display name, first name, and last name.' });
            return;
        }

        try {
            setSubmitting(true);
            const res = await api.post('/user/newUser', {
                display: normalizedDisplay,
                firstName: normalizedFirstName,
                lastName: normalizedLastName,
                email: normalizedEmail,
                role,
                password
            });
            if (res?.data?.pending) {
                navigate('/signup-pending');
            } else if (res?.data?.created && res?.data?.token) {
                setUser(res.data);
                localStorage.setItem('user', JSON.stringify(res.data));
                navigate('/');
            } else {
                setMessage({ type: 'error', text: 'Unable to submit request. Please try again.' });
            }
        } catch (e) {
            const backendError = e?.response?.data?.error;
            if (backendError === 'email already in use') {
                setMessage({ type: 'error', text: 'An account with this email already exists.' });
            } else if (backendError === 'signup already pending for this email') {
                setMessage({ type: 'error', text: 'A signup request for this email is already pending review.' });
            } else {
                setMessage({ type: 'error', text: 'Unable to submit request. Please try again.' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return <div className="auth-shell">
        {message.text && <div className={`app-notice auth-status ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`} aria-live="polite">{message.text}</div>}
        <div className="auth-card">
            <div className="auth-header">
                <h3>Create Your Account</h3>
                <p>Set up your profile to start tracking properties, bookings, and expenses.</p>
            </div>

            <form className="auth-form" onSubmit={send}>
                <div className="user-input-area auth-fields">
                    <label className="auth-label" htmlFor="signup-display">Display Name</label>
                    <TextInput id="signup-display" onChange={setDisplay} value={display} placeholder={'e.g. David B'} autoComplete="nickname" required />
                    <p className="auth-field-hint">This is the name other users will see in messages and account areas.</p>

                    <label className="auth-label" htmlFor="signup-firstname">First Name</label>
                    <TextInput id="signup-firstname" onChange={setFirstName} value={firstName} placeholder={'First Name'} autoComplete="given-name" required />

                    <label className="auth-label" htmlFor="signup-lastname">Last Name</label>
                    <TextInput id="signup-lastname" onChange={setLastName} value={lastName} placeholder={'Last Name'} autoComplete="family-name" required />

                    <label className="auth-label" htmlFor="signup-email">Email</label>
                    <TextInput id="signup-email" onChange={setEmail} value={email} type="email" placeholder={'you@email.com'} autoComplete="email" required />

                    <label className="auth-label" htmlFor="signup-password">Password</label>
                    <TextInput id="signup-password" onChange={setPassword} value={password} type="password" placeholder={'At least 6 characters'} autoComplete="new-password" required />
                    <p className="auth-field-hint">Use at least six characters. Press Enter to submit the form.</p>

                    <label className="auth-label" htmlFor="signup-role">I am signing up as</label>
                    <select id="signup-role" className="auth-select" value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="host">Host</option>
                        <option value="guest">Guest</option>
                    </select>
                </div>

                <div className="auth-actions auth-actions-spread">
                    <div className="auth-footer-copy">
                        <span>Already have an account?</span>
                        <Link to="/login" className="auth-inline-link">Log in</Link>
                    </div>
                    <Button text={submitting ? 'Creating Account...' : 'Create Account'} type="submit" disabled={submitting} />
                </div>
            </form>
        </div>
    </div>;
};

export default SignUp;